import { getPropClassesFromInstance } from 'lib/getPropClasses';
import cx from 'classnames';
import * as React from 'react';

import './styles/BoxBody.scss';

/**
 * ## Content area for use within a [Box](/internal/components/Box).
 *
 * # Common Usage
 * ```example
 * <Box>
 *   <BoxHeader title="Box" />
 *   <BoxBody>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 *   <BoxFooter>
 *     <Button>Button</Button>
 *   </BoxFooter>
 * </Box>
 * ```
 *
 * ```example
 * <Box>
 *   <BoxHeader title="Box" />
 *   <BoxBody divided>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 * </Box>
 * ```
 *
 * ```example
 * <Box>
 *   <BoxHeader title="Box" />
 *   <BoxBody divided>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 *   <BoxBody divided>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 * </Box>
 * ```
 *
 * ```example
 * <Box>
 *   <BoxBody size="small">
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 * </Box>
 * ```
 *
 * ```example
 * <Box>
 *   <BoxBody full>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </BoxBody>
 * </Box>
 * ```
 */

type BoxBodySize = 'xxsmall' | 'xsmall' | 'small' | 'medium';
type BoxBodyMargin = 'small' | 'medium';

type Props = {
  dark?: boolean;
  light?: boolean;
  /** `border-top` */
  divided?: boolean;
  /** Remove `padding` */
  full?: boolean;
  flush?: boolean;
  /** `padding` size */
  size?: BoxBodySize;
  /** `margin-left` and `margin-right` */
  margin?: BoxBodyMargin;
  title?: string;
  children: React.ReactNode;
  className?: string;
  rounded?: boolean;
  'data-testid'?: string;
};

type DefaultProps = {
  size: BoxBodySize;
};

class BoxBody extends React.Component<Props> {
  static propClasses = {
    dark: 'BoxBody--dark',
    light: 'BoxBody--light',
    divided: 'BoxBody--divided',
    full: 'BoxBody--full',
    flush: 'BoxBody--flush',
    size: {
      small: 'BoxBody--small',
      xsmall: 'BoxBody--xsmall',
      xxsmall: 'BoxBody--xxsmall',
    },
    margin: {
      small: 'BoxBody--marginSmall',
      medium: 'BoxBody--marginMedium',
    },
    rounded: 'BoxBody--rounded',
  };

  static defaultProps: DefaultProps = {
    size: 'medium',
  };

  render() {
    const { children, className } = this.props;

    return (
      <div
        className={cx('BoxBody', getPropClassesFromInstance(this), className)}
        data-testid={this.props['data-testid']}
      >
        {children}
      </div>
    );
  }
}

export default BoxBody;
